import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";

interface MarkdownEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
}

export default function MarkdownEditor({
  value,
  onChange,
  placeholder = "내용을 입력하세요...",
}: MarkdownEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const isLocalChange = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown.configure({ html: false, transformPastedText: true, transformCopiedText: true }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[240px] p-5 text-gray-700",
      },
    },
  });

  // 타이핑 → onChange 전달
  useEffect(() => {
    if (!editor) return;
    const handleUpdate = () => {
      isLocalChange.current = true;
      onChangeRef.current(editor.storage.markdown.getMarkdown());
    };
    editor.on("update", handleUpdate);
    return () => { editor.off("update", handleUpdate); };
  }, [editor]);

  // 외부 value 변경 시 (페이지 전환) 에디터 동기화
  useEffect(() => {
    if (!editor) return;
    if (isLocalChange.current) {
      isLocalChange.current = false;
      return;
    }
    if (editor.storage.markdown.getMarkdown() !== value) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  return (
    <div className="relative">
      {editor && !editor.getText() && (
        <p className="absolute top-5 left-5 text-sm text-gray-300 pointer-events-none select-none">
          {placeholder}
        </p>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
